import { useCallback } from 'react';
import { type CreateMeetingParams, useUser } from '../../../contexts';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { createMeetingAccessPayload } from '../../../utils/meetingAccessPayload';
import { mapStateToUpdatePayload } from '../helpers/formatters';
import { generateNextMeetingTitle } from '../helpers/generators';
import { sanitizeMeetingData } from '../helpers/sanitizers';
import type { PersonalMeetingData, UpdateFieldOptions, UseMeetingMutationsParams } from '../types';

const useMeetingMutations = ({
  personalMeetings,
  selectedMeeting,
  meetingData,
  userPermissions,
  onRoomChange,
  toastHelpers,
  setDefaultMeetingId,
  isDefaultMeeting,
  createMeetingMutation,
  updateMeetingMutation,
  deleteMeetingMutation,
  maxPersonalMeetings,
}: UseMeetingMutationsParams) => {
  const { handleComponentError } = useErrorHandler('PersonalMeetingsModal');
  const { userEmail } = useUser();

  const updateMeetingData = useCallback(
    (
      updater: (prev: PersonalMeetingData) => PersonalMeetingData,
      { successMessage }: UpdateFieldOptions = {},
    ) => {
      if (!(selectedMeeting && meetingData)) {
        return;
      }

      const updatedState = updater({ ...meetingData });
      const sanitizedState = sanitizeMeetingData(updatedState);
      const payload = mapStateToUpdatePayload(selectedMeeting, sanitizedState, userEmail);

      updateMeetingMutation(payload)
        .then((updated) => {
          toastHelpers.showSuccess(successMessage ?? 'Personal meeting updated');
          if (updated.room !== selectedMeeting.room) {
            onRoomChange(updated.room);
          }
        })
        .catch((err) => {
          handleComponentError(err as Error, 'update_meeting');
          toastHelpers.showError('Unable to save changes', (err as Error).message);
        });
    },
    [
      selectedMeeting,
      meetingData,
      updateMeetingMutation,
      toastHelpers,
      handleComponentError,
      onRoomChange,
      userEmail,
    ],
  );

  const toggleDefaultMeeting = useCallback(
    async (checked: boolean) => {
      if (!selectedMeeting) {
        return;
      }

      await setDefaultMeetingId(
        checked ? selectedMeeting.room : undefined,
        checked ? (selectedMeeting.password ?? '') : '',
      );
    },
    [selectedMeeting, setDefaultMeetingId],
  );

  const createMeeting = useCallback(() => {
    if (personalMeetings.length >= maxPersonalMeetings) {
      toastHelpers.showWarning('Limit reached', 'You have used all available IDs.');
      return;
    }
    if (!userPermissions.canScheduleMeetings) {
      toastHelpers.showError('Insufficient permissions', 'Your account cannot create meetings.');
      return;
    }

    const description = generateNextMeetingTitle(personalMeetings, maxPersonalMeetings);
    const userDomain = userEmail?.split('@')[1] || '';

    const params: CreateMeetingParams = {
      description,
      password: '',
      ...createMeetingAccessPayload('standard', userDomain),
    };

    createMeetingMutation(params)
      .then((newMeeting) => {
        toastHelpers.showSuccess('Personal meeting created');
        onRoomChange(newMeeting.room);
      })
      .catch((err) => {
        handleComponentError(err as Error, 'create_meeting');
        toastHelpers.showError('Unable to create meeting', (err as Error).message);
      });
  }, [
    personalMeetings,
    maxPersonalMeetings,
    userPermissions,
    createMeetingMutation,
    toastHelpers,
    handleComponentError,
    onRoomChange,
    userEmail,
  ]);

  const deleteMeeting = useCallback(
    (room: string) => {
      return deleteMeetingMutation(room)
        .then(() => {
          if (isDefaultMeeting(room)) {
            void setDefaultMeetingId(undefined);
          }

          toastHelpers.showSuccess('Personal meeting deleted');
        })
        .catch((err) => {
          handleComponentError(err as Error, 'delete_meeting');
          toastHelpers.showError('Unable to delete meeting', (err as Error).message);
        });
    },
    [
      deleteMeetingMutation,
      toastHelpers,
      handleComponentError,
      setDefaultMeetingId,
      isDefaultMeeting,
    ],
  );

  return {
    createMeeting,
    deleteMeeting,
    updateMeetingData,
    toggleDefaultMeeting,
  };
};

export default useMeetingMutations;
